import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState, useCallback } from 'react'
import { useQuery as useConvexQuery, useAction } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useAppSelector, useAppDispatch } from '@/hooks/useRedux'
import {
  startSongReviewSession,
  submitGrade,
  completeSongReviewSession,
  markSessionSaved,
  clearSongReviewSession,
  selectSongReviewSession,
  selectCurrentFlashcard,
  selectSessionProgress,
  selectSessionNeedsSave,
  selectAverageGrade,
  type AnkiGrade,
} from '@/store/slices/songReviewSessionSlice'
import { buildSongReviewPayload } from '@/store/middleware/songReviewAutoSaveMiddleware'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { WaveSurferPlayer, type FlashcardRegion } from '@/components/WaveSurferPlayer'
import { SongReviewCard } from '@/components/SongReviewCard'
import { SongReviewGradeButtons } from '@/components/SongReviewGradeButtons'
import { SongReviewProgress } from '@/components/SongReviewProgress'
import { ArrowLeft, Save, CheckCircle, AlertCircle, X } from 'lucide-react'

export const Route = createFileRoute('/app/song-review/$songId')({
  component: SongReviewSessionPage,
})

function SongReviewSessionPage() {
  const { songId } = Route.useParams()
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to review songs</div>
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <SongReviewContent userId={currentUser._id} songId={songId} />
    </ErrorBoundary>
  )
}

function SongReviewContent({ userId, songId }: { userId: string; songId: string }) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const session = useAppSelector(selectSongReviewSession)
  const currentFlashcard = useAppSelector(selectCurrentFlashcard)
  const progress = useAppSelector(selectSessionProgress)
  const needsSave = useAppSelector(selectSessionNeedsSave)
  const averageGrade = useAppSelector(selectAverageGrade)
  const submitSongReviewAction = useAction(api.flashcards.submitSongReview)

  const [isFlipped, setIsFlipped] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [showResumeNotice, setShowResumeNotice] = useState(false)

  // Fetch song data if session not active
  const songsForReview = useConvexQuery(
    api.flashcards.getSongForReview,
    session.isActive ? 'skip' : {
      userId: userId as any,
      limit: 20,
    }
  )

  // Initialize or resume session when song data loads
  useEffect(() => {
    // If session is active for a different song, clear it
    if (session.isActive && session.songId !== songId) {
      dispatch(clearSongReviewSession())
      return
    }

    // If session is active for this song, resume automatically (no action needed)
    if (session.isActive && session.songId === songId) {
      // Show resume notice if there are already graded cards
      if (session.grades.length > 0 && !showResumeNotice) {
        setShowResumeNotice(true)
      }
      return // Continue from current position
    }

    // No active session - start new one
    if (!session.isActive && songsForReview) {
      const song = songsForReview.find((s) => s.songId === songId)
      if (song) {
        dispatch(startSongReviewSession({
          sessionId: `session-${Date.now()}`,
          songId: song.songId,
          songTitle: song.songTitle,
          songArtist: song.songArtist,
          fullSongUrl: song.fullSongUrl || undefined,
          flashcards: song.flashcards.map(fc => ({
            _id: fc._id,
            frontSpanish: fc.frontSpanish,
            backEnglish: fc.backEnglish,
            audioTimestamp: fc.audioTimestamp,
            easeFactor: fc.easeFactor,
            interval: fc.interval,
            repetitions: fc.repetitions,
            dueDate: fc.dueDate,
          })),
        }))
      }
    }
  }, [session.isActive, session.songId, songsForReview, songId, dispatch])

  // Auto-save effect with retry logic and error recovery
  useEffect(() => {
    if (needsSave && !isSaving) {
      const saveSessionWithRetry = async () => {
        setIsSaving(true)
        setSaveError(null)

        const maxRetries = 3
        const retryDelays = [1000, 2000, 4000] // Exponential backoff: 1s, 2s, 4s

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            const payload = buildSongReviewPayload(session)
            await submitSongReviewAction(payload as any)
            // Mark as saved after successful save
            dispatch(markSessionSaved())
            setSaveError(null) // Clear any previous errors
            break // Success - exit retry loop
          } catch (error) {
            console.error(`Save attempt ${attempt + 1} failed:`, error)

            if (attempt < maxRetries) {
              // Wait before retrying (exponential backoff)
              await new Promise(resolve => setTimeout(resolve, retryDelays[attempt]))
            } else {
              // All retries failed - show error to user
              const errorMessage = error instanceof Error ? error.message : 'Unknown error'
              setSaveError(`Failed to save session after ${maxRetries + 1} attempts: ${errorMessage}`)
            }
          }
        }

        setIsSaving(false)
      }

      // Debounce save by 2 seconds
      const timer = setTimeout(saveSessionWithRetry, 2000)
      return () => clearTimeout(timer)
    }
  }, [needsSave, session, submitSongReviewAction, isSaving, dispatch])

  // Reset flip state when card changes
  useEffect(() => {
    setIsFlipped(false)
  }, [currentFlashcard?._id])

  // Auto-dismiss error notifications after 10 seconds
  useEffect(() => {
    if (saveError) {
      const timer = setTimeout(() => {
        setSaveError(null)
      }, 10000)
      return () => clearTimeout(timer)
    }
  }, [saveError])

  // Auto-dismiss resume notice after 5 seconds
  useEffect(() => {
    if (showResumeNotice) {
      const timer = setTimeout(() => {
        setShowResumeNotice(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [showResumeNotice])

  const handleGrade = useCallback(async (grade: AnkiGrade) => {
    if (!currentFlashcard) return

    dispatch(submitGrade({
      flashcardId: currentFlashcard._id,
      grade,
    }))

    // Check if session complete
    if (progress.reviewed + 1 >= progress.total) {
      // Save final session with retry logic
      setIsSaving(true)
      setSaveError(null)

      const maxRetries = 3
      const retryDelays = [1000, 2000, 4000] // Exponential backoff

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const payload = buildSongReviewPayload(session)
          await submitSongReviewAction(payload as any)
          dispatch(completeSongReviewSession())
          navigate({ to: '/app/song-review' })
          return // Success - exit function
        } catch (error) {
          console.error(`Session completion attempt ${attempt + 1} failed:`, error)

          if (attempt < maxRetries) {
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, retryDelays[attempt]))
          } else {
            // All retries failed - show error and allow user to continue
            const errorMessage = error instanceof Error ? error.message : 'Unknown error'
            setSaveError(`Failed to complete session after ${maxRetries + 1} attempts: ${errorMessage}`)
            setIsSaving(false)
          }
        }
      }
    }
  }, [currentFlashcard, dispatch, progress, session, submitSongReviewAction, navigate])

  // Keyboard shortcuts for grading (0-5 keys)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only allow grading if card is flipped
      if (!isFlipped) return

      // Check if a grade key was pressed (0-5)
      const key = event.key
      if (/^[0-5]$/.test(key)) {
        event.preventDefault()
        const grade = parseInt(key) as AnkiGrade
        handleGrade(grade)
      }

      // Also support spacebar to flip card
      if (event.code === 'Space' && !isFlipped) {
        event.preventDefault()
        setIsFlipped(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isFlipped, handleGrade])

  // Build waveform regions from all flashcards
  const waveformRegions: FlashcardRegion[] = session.flashcards
    .filter(fc => fc.audioTimestamp)
    .map(fc => ({
      id: `flashcard-${fc._id}`,
      start: fc.audioTimestamp!.startTime * 1000,
      end: fc.audioTimestamp!.endTime * 1000,
      text: fc.frontSpanish.join(' '),
      color: fc._id === currentFlashcard?._id ? '#00ffff' : '#666666',
    }))

  if (!session.isActive || !currentFlashcard) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading session...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white py-4 px-4">
      {/* Error Toast Notification */}
      {saveError && (
        <div className="fixed top-4 right-4 max-w-md z-50 animate-in slide-in-from-top">
          <div className="terminal-border border-red-500 bg-black p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="font-mono font-bold text-red-500 mb-1">Save Failed</h3>
                <p className="font-mono text-sm text-red-400 break-words">{saveError}</p>
              </div>
              <button
                onClick={() => setSaveError(null)}
                className="flex-shrink-0 text-red-500 hover:text-red-400 transition-colors"
                aria-label="Dismiss error"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume Session Notice */}
      {showResumeNotice && (
        <div className="fixed top-4 left-4 max-w-md z-50 animate-in slide-in-from-top">
          <div className="terminal-border border-terminal-green bg-black p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-terminal-green flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="font-mono font-bold text-terminal-green mb-1">Session Resumed</h3>
                <p className="font-mono text-sm text-trail-tan">
                  Continuing from card {progress.reviewed + 1} of {progress.total}
                </p>
              </div>
              <button
                onClick={() => setShowResumeNotice(false)}
                className="flex-shrink-0 text-terminal-green hover:text-sky-blue transition-colors"
                aria-label="Dismiss notice"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-4 terminal-border p-4">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => navigate({ to: '/app/song-review' })}
              className="flex items-center gap-2 px-3 py-2 bg-trail-tan/10 border-2 border-trail-tan text-trail-tan hover:bg-trail-tan hover:text-black transition-colors font-mono"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Songs</span>
            </button>
            <div className="flex items-center gap-3">
              {isSaving && (
                <div className="flex items-center gap-2 text-terminal-green font-mono text-sm">
                  <Save className="w-4 h-4 animate-pulse" />
                  <span>Saving...</span>
                </div>
              )}
              {!needsSave && !isSaving && progress.reviewed > 0 && (
                <div className="flex items-center gap-2 text-terminal-green font-mono text-sm">
                  <CheckCircle className="w-4 h-4" />
                  <span>Saved</span>
                </div>
              )}
            </div>
          </div>
          <SongReviewProgress
            songTitle={session.songTitle}
            songArtist={session.songArtist}
            progress={progress}
            averageGrade={averageGrade}
          />
        </div>

        {/* Waveform Player */}
        {session.fullSongUrl && (
          <div className="mb-4">
            <WaveSurferPlayer
              key={`waveform-${session.songId}`}
              audioUrl={session.fullSongUrl}
              regions={waveformRegions}
              onRegionClick={() => {}}
              height={128}
              wholeSongMode={true}
            />
          </div>
        )}

        {/* Flashcard */}
        <SongReviewCard
          flashcard={currentFlashcard}
          isFlipped={isFlipped}
          onFlip={() => setIsFlipped(!isFlipped)}
        />

        {/* Grade Buttons */}
        <SongReviewGradeButtons isFlipped={isFlipped} onGrade={handleGrade} />
      </div>
    </div>
  )
}
